// @owners { team: patients }
import { ActionSheetContext, Body, Button, LgPadding, ListDescription, ListItem } from '@alto/design-system';
import { ConfirmDeleteActionSheet, PaymentMethodIcon } from '@alto/features';
import React, { useContext } from 'react';
import { getPaymentMethodIconName } from '~shared/features/payment-methods/helpers';
import { paymentType } from '~shared/features/payment-methods/helpers/paymentType';
import { shouldShowExpiration } from '~shared/helpers/payment';
import { type PaymentMethod } from '~shared/types';

type Props = {
  readonly creditCardPaymentMethods: PaymentMethod[];
  readonly deletePaymentMethod: (paymentMethod: PaymentMethod) => Promise<void>;
};

export const CreditCardPayments: React.FC<Props> = ({ creditCardPaymentMethods, deletePaymentMethod }) => {
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);

  if (creditCardPaymentMethods.length > 0) {
    return (
      <>
        {creditCardPaymentMethods.map((creditCardPaymentMethod) => {
          const card = creditCardPaymentMethod.details;
          const cardIdentity = `${card?.card_type || ''} ${card?.card_last_4 || ''}`.trim();
          const paymentMethodType = paymentType(creditCardPaymentMethod);
          const showExpiration = shouldShowExpiration(paymentMethodType);
          const cardExpiration =
            showExpiration && card?.card_expiration_month && card.card_expiration_year
              ? `Expires ${card.card_expiration_month}/${card.card_expiration_year}`
              : undefined;
          const paymentMethodIconName = getPaymentMethodIconName(creditCardPaymentMethod);

          return (
            <ListItem
              key={`cc-${creditCardPaymentMethod.id}`}
              verticalAlignment="top"
              title={cardIdentity}
              descriptions={cardExpiration ? <ListDescription>{cardExpiration}</ListDescription> : undefined}
              LeftContent={
                <PaymentMethodIcon
                  paymentMethodIconName={paymentMethodIconName}
                  size="default"
                />
              }
              RightContent={
                <Button
                  type="tertiary"
                  small
                  noPadding
                  label="DELETE"
                  accessibilityLabel={`Press to delete credit card ending in ${card?.card_last_4}`}
                  onPress={() => {
                    setActiveActionSheet(
                      <ConfirmDeleteActionSheet
                        analyticsName="confirm delete credit card"
                        description={`the credit card ending in ${card?.card_last_4}`}
                        onConfirm={() => {
                          deletePaymentMethod(creditCardPaymentMethod);
                          closeActionSheet();
                        }}
                      />,
                    );
                  }}
                />
              }
            />
          );
        })}
      </>
    );
  }

  return (
    <LgPadding>
      <Body>We don’t have a credit card on file for you yet. Go ahead and add one!</Body>
    </LgPadding>
  );
};
